import { ReactNode, useEffect, useState } from 'react';

import {
  AssetDTO,
  categoryIdToCategoryValue,
  DOCUMENT_MAX_FILE_SIZE_BYTES,
  documentToCategoryValue,
  DocumentType,
  EntityRefUtil,
  FILE_MIME_TYPES,
  formatManuallyWithUserTimezone,
  GenerateDocumentPresignedUploadUrlRequestDTO,
  GetDocumentsByAssetResponseDTO,
  getFileExtension,
  getMimeTypeFromExtension,
  parseCategoryValue,
  RecordState,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startCase } from 'lodash';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetPortal, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowLeftIcon } from '@/legacy/components/icons/arrow-left';
import { cn, initials } from '@/lib/utils';
import { assetDocumentKeys, documentKeys, getDocumentCategoriesQueryOptions } from '@/queries/documents';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { getFileTypeIcon } from '@/utils/file-type';
import { getWorkspaceRoute } from '@/utils/routes';
import { postToPresignedTarget } from '@/utils/upload';

import { ArchiveConfirmationDialog } from '../common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '../common/delete-confirmation-dialog';
import { RestoreConfirmationDialog } from '../common/restore-confirmation-dialog';
import ArchiveIcon from '../icons/archive';
import AttachmentIcon from '../icons/attachment';
import CloseIcon from '../icons/close';
import DeleteIcon from '../icons/delete';
import UndoIcon from '../icons/undo';
import { AsyncSelect } from '../ui/async-select';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { DatePicker } from '../ui/date-picker';
import AddDocumentUploadModal, { AddDocumentUploadResult } from './add-document-upload-modal';

const documentFormSchema = z.object({
  asset: z.object({
    value: z.string(),
    label: z.string(),
  }),
  name: z.string().min(1, 'Required'),
  category: z.string({ error: 'Required' }),
  expiry: z.date().optional(),
  mimeType: z.string().optional(),
  url: z.string().min(1, 'Required'),
  comments: z.string().optional(),
});

type DocumentFormData = z.infer<typeof documentFormSchema>;

const ACCEPTED_FILE_TYPES = Object.fromEntries(Object.entries(FILE_MIME_TYPES).map(([mimeType, extension]) => [mimeType, [`.${extension}`]]));
const ACCEPTED_FILE_TYPES_LABEL = [...new Set(Object.values(FILE_MIME_TYPES))].map((extension) => `.${extension}`).join(', ');

// The "category" field is a composite value (`type:<DocumentType>` /
// `category:<uuid>`) so a single Select can drive either a not-yet-migrated
// legacy document (still on the raw `type` column) or a real, workspace-
// editable category — see @gearbelt/global's document-category-value util
// for the shared encode/decode helpers. The 4 auto-assigned system types
// (Issue, Repair Record, Service Record, Inspection) are real category rows
// too now, so they arrive through `documentCategories` below like any other
// category — no separate hardcoded option list.

export default function DocumentDrawer({
  open: openProp,
  onOpenChange,
  asset,
  document,
  noTrigger = false,
  readOnly = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  asset?: AssetDTO;
  document?: GetDocumentsByAssetResponseDTO['data'][number] | null;
  noTrigger?: boolean;
  readOnly?: boolean;
}) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const org = useUserStore.getState().org;
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isAWA } = useIsAWA();

  const { data: documentCategories } = useQuery(getDocumentCategoriesQueryOptions({ orgId: org?.id }));

  const documentTypeOptions = (documentCategories ?? []).map((category) => ({ value: categoryIdToCategoryValue(category.id), label: category.name }));
  // A document created before its type/category was backfilled into the
  // workspace's category list won't match any option above — synthesize one
  // from its raw type so the existing value still displays, without forcing
  // the user to re-select it.
  const currentCategoryValue = documentToCategoryValue(document);
  const options =
    currentCategoryValue &&
    !documentTypeOptions.some((option) => option.value === currentCategoryValue) &&
    document?.type !== null &&
    document?.type !== undefined
      ? [...documentTypeOptions, { value: currentCategoryValue, label: startCase(DocumentType[document.type]) }]
      : documentTypeOptions;

  const [open, setOpen] = useState(openProp ?? false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const form = useForm<DocumentFormData>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      asset: asset ? { value: asset.id, label: EntityRefUtil.createAssetLabel(asset) } : undefined,
      name: document?.name || '',
      category: documentToCategoryValue(document),
      expiry: document?.expiry ? new Date(document.expiry) : undefined,
      comments: document?.comments || '',
    },
  });

  const { setValue, watch, clearErrors } = form;
  const watchedAsset = watch('asset');

  useEffect(() => {
    if (document) {
      form.reset({
        asset: asset ? { value: asset.id, label: EntityRefUtil.createAssetLabel(asset) } : undefined,
        name: document.name || '',
        category: documentToCategoryValue(document),
        expiry: document.expiry ? new Date(document.expiry) : undefined,
        url: document.url || '',
        comments: document.comments || '',
      });
    } else {
      form.reset({
        asset: asset ? { value: asset.id, label: EntityRefUtil.createAssetLabel(asset) } : undefined,
        name: '',
        category: undefined,
        expiry: undefined,
        url: '',
        comments: '',
      });
    }
  }, [document, form, asset]);

  useEffect(() => {
    if (asset) {
      setValue('asset', { value: asset.id, label: EntityRefUtil.createAssetLabel(asset) });
    }
  }, [asset, setValue]);

  const { mutateAsync: generatePresignedUploadUrl, isPending: isGeneratingPresignedUploadUrl } = useMutation({
    mutationFn: async (dto: GenerateDocumentPresignedUploadUrlRequestDTO) => {
      if (isAWA) {
        return await (await AWAApiV2Service.GenerateDocumentPresignedUploadUrl(dto)).json();
      } else {
        return await (await ApiService.GenerateDocumentPresignedUploadUrl(dto)).json();
      }
    },
  });

  const { mutateAsync: updateDocument, isPending: isUpdatingDocument } = useMutation({
    mutationFn: async (values: DocumentFormData) => {
      if (!document) throw new Error('No document to update');
      const { type, categoryId } = parseCategoryValue(values.category);
      return await ApiService.UpdateDocument({
        id: document.id,
        name: values.name,
        type: type ?? null,
        categoryId: categoryId ?? null,
        expiry: values.expiry?.toISOString(),
        mimeType: values.mimeType,
        url: values.url,
        comments: values.comments,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: assetDocumentKeys.all });
      await queryClient.invalidateQueries({ queryKey: documentKeys.all });
      toast.success('Document updated successfully');
      form.reset(form.getValues());
    },
    onError: (error) => {
      toast.error(`Error updating document: ${error.message}`);
    },
  });

  // Drawer-level destructive actions are the per-record path Members use to
  // archive / delete / restore documents they authored — the page-level bulk
  // buttons are Admin-only. BulkUpdateDocumentState is reused with a single
  // id; the backend's Member ownership check handles authorisation.
  const { mutateAsync: updateDocumentState, isPending: isUpdatingDocumentState } = useMutation({
    mutationFn: async (state: RecordState) => {
      if (!document) throw new Error('No document to update');
      return await ApiService.BulkUpdateDocumentState({ documentIds: [document.id], state });
    },
    onSuccess: async (_data, state) => {
      const messages = {
        [RecordState.Active]: 'Document restored successfully',
        [RecordState.Archived]: 'Document archived successfully',
        [RecordState.Deleted]: 'Document deleted successfully',
      };
      toast.success(messages[state]);
      setShowArchiveConfirm(false);
      setShowRestoreConfirm(false);
      setShowDeleteConfirm(false);
      await queryClient.invalidateQueries({ queryKey: assetDocumentKeys.all });
      await queryClient.invalidateQueries({ queryKey: documentKeys.all });
      handleOpenChange(false, true);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { mutateAsync: createDocument, isPending: isCreatingDocument } = useMutation({
    mutationFn: async (values: DocumentFormData) => {
      if (!values.url) throw new Error('File is required');
      const { type, categoryId } = parseCategoryValue(values.category);
      return await ApiService.CreateAssetDocument({
        assetId: values.asset.value,
        name: values.name,
        type,
        categoryId,
        expiry: values.expiry?.toISOString(),
        url: values.url,
        mimeType: values.mimeType,
        comments: values.comments,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: assetDocumentKeys.all });
      await queryClient.invalidateQueries({ queryKey: documentKeys.all });
      handleOpenChange(false, true);
      form.reset();
      toast.success('Document created successfully');
    },
    onError: (error) => {
      toast.error(`Error creating document: ${error.message}`);
    },
  });

  async function uploadFile(file: File): Promise<{ url: string }> {
    const values = form.getValues();
    const { upload, fileUrl } = await generatePresignedUploadUrl({
      filename: `${Date.now()}-${file.name}`,
      contentType: file.type,
      assetId: values.asset?.value,
    });

    await postToPresignedTarget(upload, file);

    return { url: fileUrl };
  }

  function handleUploadConfirm({ url, mimeType, name }: AddDocumentUploadResult) {
    form.setValue('url', url, { shouldDirty: true });
    form.setValue('mimeType', mimeType, { shouldDirty: true });
    form.setValue('name', name, { shouldDirty: true });
    clearErrors('url');
  }

  function handleOpenChange(open: boolean, success?: boolean) {
    onOpenChange?.(open, success);
    setOpen(open);
    if (!open) {
      form.reset();
      setIsUploadModalOpen(false);
    }
  }

  function handleRemoveUploadedFile() {
    setValue('url', '');
    setValue('mimeType', undefined);
    clearErrors('url');
  }

  async function handleSubmit(values: DocumentFormData) {
    // Require file
    if (!values.url) {
      form.setError('url', { type: 'manual', message: 'Required' });
      return;
    }

    if (document) {
      await updateDocument(values);
    } else {
      await createDocument(values);
    }
  }

  function handleError(errors: FieldErrors<DocumentFormData>) {
    const fieldNames = Object.keys(errors);
    if (fieldNames.length > 0) {
      const firstFieldWithError = window.document.getElementById(fieldNames[0]);
      if (firstFieldWithError) {
        firstFieldWithError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        {!noTrigger && (
          <SheetTrigger asChild>
            <Button className="h-full rounded-none p-2" variant="success">
              Create Document
            </Button>
          </SheetTrigger>
        )}
        <SheetPortal>
          <SheetContent
            onInteractOutside={(e) => {
              if (form.formState.isSubmitting || isUploadModalOpen) {
                e.preventDefault();
              }
            }}
            hideCloseIcon
            className="mx-auto h-full w-full">
            <Form {...form}>
              <form className="flex h-full flex-1 flex-col" onSubmit={form.handleSubmit(handleSubmit, handleError)}>
                <SheetHeader className="border-grey-3 flex-row items-center justify-between space-y-0 border-b bg-white">
                  <VisuallyHidden>
                    <SheetDescription>{document ? 'View Document' : 'Create Document'}</SheetDescription>
                  </VisuallyHidden>

                  {isAWA ? (
                    <>
                      <div className="flex items-center gap-2 py-1">
                        <button className="-m-2 p-2" type="button" onClick={() => handleOpenChange(false)} disabled={form.formState.isSubmitting}>
                          <ArrowLeftIcon className="size-6" />
                        </button>
                        <SheetTitle className="text-xl">{document ? 'View Document' : 'Create Document'}</SheetTitle>
                      </div>

                      {!readOnly && (
                        <div className="flex items-center gap-4">
                          <Button
                            variant="link"
                            className="text-default-green text-xl font-medium hover:no-underline"
                            size="default"
                            type="submit"
                            disabled={form.formState.isSubmitting || !form.formState.isDirty}
                            loading={isCreatingDocument || isUpdatingDocument}>
                            Save
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <SheetTitle>{document ? 'View Document' : 'Create Document'}</SheetTitle>
                      <div className="flex items-center gap-2">
                        {!readOnly && !!document && document.state === RecordState.Active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            aria-label="Archive"
                            onClick={() => setShowArchiveConfirm(true)}
                            disabled={isUpdatingDocumentState}>
                            <ArchiveIcon className="h-5 w-5" />
                          </Button>
                        )}
                        {!readOnly && !!document && document.state === RecordState.Archived && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              aria-label="Delete"
                              className="text-default-red"
                              onClick={() => setShowDeleteConfirm(true)}
                              disabled={isUpdatingDocumentState}>
                              <DeleteIcon className="h-5 w-5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              aria-label="Restore"
                              onClick={() => setShowRestoreConfirm(true)}
                              disabled={isUpdatingDocumentState}>
                              <UndoIcon className="h-5 w-5" />
                            </Button>
                          </>
                        )}
                        {!readOnly && (
                          <Button
                            variant="success"
                            size="default"
                            type="submit"
                            disabled={form.formState.isSubmitting || !form.formState.isDirty}
                            loading={isCreatingDocument || isUpdatingDocument}>
                            Save
                          </Button>
                        )}
                        <button
                          type="button"
                          className="data-[state=open]:bg-default-blue rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                          disabled={form.formState.isSubmitting}
                          onClick={() => handleOpenChange(false)}>
                          <CloseIcon className="h-4 w-4" />
                          <span className="sr-only">Close</span>
                        </button>
                      </div>
                    </>
                  )}
                </SheetHeader>

                {!!document && (
                  <div className="flex flex-col gap-1 px-4 pt-4">
                    <div className="flex items-center justify-end gap-2 text-sm">
                      <div className="flex items-center gap-2 text-right">
                        <div className="flex flex-col gap-[2px]">
                          <div className="line-clamp-1 font-medium">{EntityRefUtil.createUserFullNameRef(document.user, '-')}</div>
                          <div className="text-secondary-text">
                            {formatManuallyWithUserTimezone(new Date(document.createdAt), 'MMM DD, YYYY hh:mm a', userTimezone)}
                          </div>
                        </div>
                        <Avatar>
                          {document.user && document.user.avatarUrl ? (
                            <AvatarImage src={document.user.avatarUrl} />
                          ) : (
                            <AvatarFallback>{initials(EntityRefUtil.createUserFullNameRef(document.user, '-'))}</AvatarFallback>
                          )}
                        </Avatar>
                      </div>
                    </div>

                    <div className="border-default-blue border-b-4" />
                  </div>
                )}

                <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
                  {/* Asset */}
                  {!asset && (
                    <Controller
                      control={form.control}
                      name="asset"
                      render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <div>
                          <Label required>Asset</Label>
                          <AsyncSelect
                            cacheOptions
                            defaultOptions
                            value={value}
                            onChange={onChange}
                            className={cn('w-full')}
                            blurInputOnSelect
                            placeholder="Select asset..."
                            aria-label="Asset"
                            error={!!error}
                            loadOptions={async (input: string) => {
                              const { data } = await (await ApiService.GetAssets({ limit: 100, filters: { search: input, state: RecordState.Active } })).json();
                              return data?.map((asset) => ({ value: asset.id, label: EntityRefUtil.createAssetLabel(asset) }));
                            }}
                          />
                          {error && <div className="text-default-red text-sm">{error.message}</div>}
                        </div>
                      )}
                    />
                  )}

                  {!!document && !!asset && (
                    <div className="flex flex-col gap-1">
                      <Label>Asset</Label>
                      <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                        {isAWA ? (
                          <div className="text-sm">{EntityRefUtil.createAssetLabel(asset)}</div>
                        ) : (
                          <Link className="text-default-blue text-sm underline" to={getWorkspaceRoute(`/assets/${asset.id}`)}>
                            {EntityRefUtil.createAssetLabel(asset)}
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                  {watchedAsset && (
                    <>
                      {/* Document Category */}
                      <Controller
                        control={form.control}
                        name="category"
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                          <div>
                            <Label required>Document category</Label>
                            <Select
                              error={!!error}
                              value={options.find((option) => option.value === value)}
                              onChange={(val) => onChange(val?.value)}
                              options={options}
                              className={cn('w-full')}
                              blurInputOnSelect
                              placeholder="Select..."
                              aria-label="Document category"
                              isDisabled={readOnly}
                            />
                            {error && <div className="text-default-red mt-1 text-sm">{error.message}</div>}
                          </div>
                        )}
                      />

                      {/* Expiry Date */}
                      <Controller
                        control={form.control}
                        name="expiry"
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                          <div>
                            <Label>Expiry Date</Label>
                            <DatePicker error={!!error} date={value} setDate={onChange} placeholder="Select date..." disabled={readOnly} useUtc={true} />
                            {error && <div className="text-default-red mt-1 text-sm">{error.message}</div>}
                          </div>
                        )}
                      />

                      {/* Attachment */}
                      <Controller
                        control={form.control}
                        name="url"
                        render={({ field: { value }, fieldState: { error } }) => (
                          <div className="flex break-inside-avoid flex-col" id="attachments-select">
                            <Label required>Document</Label>
                            {error && <div className="text-default-red mt-1 text-sm">{error.message}</div>}
                            {/* Uploaded attachment */}
                            <div className="mt-2 flex flex-col gap-2">
                              {value && (
                                <FileItem
                                  url={value === document?.url ? (document?.presignedDownloadUrl ?? value) : value}
                                  filename={form.watch('name') || `document`}
                                  onDelete={handleRemoveUploadedFile}
                                  readOnly={readOnly}
                                />
                              )}

                              {!!document && value?.length === 0 && <div className="text-secondary-text text-sm">No document uploaded</div>}

                              {!(isAWA && !!document) && (
                                <>
                                  <TooltipProvider>
                                    <Tooltip key={`tooltip-${!!value}`} delayDuration={0}>
                                      <TooltipTrigger asChild>
                                        <div className="mt-1 w-fit">
                                          <Button
                                            className="leading-none print:hidden"
                                            icon={<AttachmentIcon />}
                                            type="button"
                                            disabled={isGeneratingPresignedUploadUrl || !!value}
                                            onClick={() => setIsUploadModalOpen(true)}>
                                            Add Document
                                          </Button>
                                        </div>
                                      </TooltipTrigger>
                                      {!!value && (
                                        <TooltipPortal>
                                          <TooltipContent side="bottom" className="bg-default-text pointer-events-none border-none text-xs text-white">
                                            <TooltipArrow className="fill-default-text" />
                                            Only one file allowed
                                          </TooltipContent>
                                        </TooltipPortal>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      />

                      {/* Document Name */}
                      {!!form.watch('url') && (
                        <Controller
                          control={form.control}
                          name="name"
                          render={({ field: { value, onChange }, fieldState: { error } }) => {
                            const extension = getFileExtension(form.watch('url'));
                            const suffix = extension ? `.${extension}` : '';
                            const baseName = suffix && value.endsWith(suffix) ? value.slice(0, -suffix.length) : value;
                            return (
                              <div>
                                <Label required>Document Name</Label>
                                <Input
                                  value={baseName}
                                  onChange={(e) => onChange(e.target.value ? `${e.target.value}${suffix}` : '')}
                                  disabled={readOnly}
                                  aria-label="Document Name"
                                  suffix={extension ? <span className="text-secondary-text text-sm">.{extension}</span> : undefined}
                                />
                                {error && <div className="text-default-red mt-1 text-sm">{error.message}</div>}
                              </div>
                            );
                          }}
                        />
                      )}

                      {/* Comments */}
                      <div>
                        <Label>Comments</Label>
                        <Controller
                          control={form.control}
                          name="comments"
                          render={({ field }) => <Textarea {...field} placeholder="Enter comments" rows={3} disabled={readOnly} />}
                        />
                      </div>
                    </>
                  )}
                </div>
              </form>
            </Form>
          </SheetContent>
        </SheetPortal>
      </Sheet>

      <AddDocumentUploadModal
        open={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        onConfirm={handleUploadConfirm}
        uploadFile={uploadFile}
        acceptedFileTypes={ACCEPTED_FILE_TYPES}
        acceptedFileTypesLabel={ACCEPTED_FILE_TYPES_LABEL}
        maxFileSizeBytes={DOCUMENT_MAX_FILE_SIZE_BYTES}
      />
      <ArchiveConfirmationDialog
        open={showArchiveConfirm}
        onOpenChange={setShowArchiveConfirm}
        callback={() => updateDocumentState(RecordState.Archived)}
        loading={isUpdatingDocumentState}
        title="Archive document?"
        description="This document will be moved to archived. You can restore it later."
      />
      <RestoreConfirmationDialog
        open={showRestoreConfirm}
        onOpenChange={setShowRestoreConfirm}
        callback={() => updateDocumentState(RecordState.Active)}
        loading={isUpdatingDocumentState}
        title="Restore document?"
        description="This document will be moved back to active."
      />
      <DeleteConfirmationDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        callback={() => updateDocumentState(RecordState.Deleted)}
        loading={isUpdatingDocumentState}
        title="Delete document?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete this document?"
      />
    </>
  );
}

function FileItem({
  filename,
  onDelete,
  icon,
  url,
  readOnly = false,
}: {
  filename: string;
  onDelete?: () => void;
  icon?: ReactNode;
  url?: string;
  readOnly?: boolean;
}) {
  const { isAWA } = useIsAWA();
  const Element = url ? 'a' : 'div';

  return (
    <Element
      href={url || undefined}
      target={url ? '_blank' : undefined}
      rel={url ? 'noreferrer' : undefined}
      className="border-grey-3 flex items-center justify-between border-b p-2">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="relative flex-shrink-0">{icon || getFileTypeIcon(getMimeTypeFromExtension(filename.split('.').pop() ?? '') ?? '')}</div>
        <span className="line-clamp-1 min-w-0 flex-1 text-sm font-medium">{filename}</span>
      </div>
      {!isAWA && onDelete && (
        <Button
          type="button"
          variant="outline"
          className="text-default-red flex items-center gap-1 border-none outline-none print:hidden"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
          disabled={readOnly}>
          <DeleteIcon className="h-4 w-4" />
          Delete
        </Button>
      )}
    </Element>
  );
}
